import { useEffect, useRef, useState, useContext, useMemo } from 'react';
import { DataGridPro, GridActionsCellItem, useGridApiRef, GridRowModes } from '@mui/x-data-grid-pro';
import { Select, MenuItem } from '@mui/material';
import apiAdmin from '../../services/api-admin';
import { MdDelete, MdAdd, MdOutlineStarRate } from 'react-icons/md';
import CustomDataGridToolbar from '../layout/custom-toolbar';
import { AdminUserContext } from '../../contexts/user-context';
import { FilePath, FormatNameToFilePath } from '../../services/utilities';
import "./reps-of-the-year-grid.scss";

const RepsOfTheYear = () => {
    const { adminUserProfile } = useContext(AdminUserContext);
    const [repToDelete, setRepToDelete] = useState(null);
    const [saved, setSaved] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [error, setError] = useState(null);
    const apiRef = useGridApiRef();
    const [rows, setRows] = useState([]);
    const [modifiedRows, setModifiedRows] = useState([]);
    const [salesReps, setSalesReps] = useState([]);
    const [loading, setLoading] = useState(true);
    const idCounterRef = useRef(0);
    const [rowModesModel, setRowModesModel] = useState({});
    const isManagementUser = !!adminUserProfile?.memberOf?.includes("ManagementUsers");
  
    const { maxOrders, maxRevenue } = useMemo(() => {
        const maxOrders = Math.max(1, ...rows.map(r => Number(r.orderCount ?? 0)));
        const maxRevenue = Math.max(1, ...rows.map(r => Number(r.revenue ?? 0)));
        return { maxOrders, maxRevenue };
    }, [rows]);

    const rankByRepId = useMemo(() => {
        const scored = rows.map(r => {
            const orders = Number(r.orderCount ?? 0);
            const revenue = Number(r.revenue ?? 0);
            const score = 0.5 * (orders / maxOrders) + 0.5 * (revenue / maxRevenue);
            return { repId: r.repId, score };
        });

        scored.sort((a, b) => b.score - a.score);

        const map = new Map();
        scored.forEach((x, i) => map.set(x.repId, i + 1));
        return map;
    }, [rows, maxOrders, maxRevenue]);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);

                await getRepsOfTheYear();
                const { data } = await apiAdmin.get('/team-members');
                setSalesReps(data);
            } catch {
                setError("There was an error loading data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const getRepsOfTheYear = async () => {
        const { data } = await apiAdmin.get('/reps-of-the-year');
             setRows(data.map((a) => ({
                    repId: a.repId,
                    name: a.name,
                    position: a.position,
                    revenue: Number(a.revenue ?? 0),
                    lastUpdated: a.lastUpdated ? new Date(a.lastUpdated).toLocaleDateString() : null,
                    region: a.region,
                    orderCount: a.orderCount
             })));
        
    }

    const repByName = useMemo(() => {
        const m = new Map();
        for (const r of salesReps) m.set(`${r.firstName} ${r.lastName}`, r);
        return m;
    }, [salesReps]);

    const getRegionName = (regionId) => {
        if (regionId === 1 || regionId === 9) return "Southwest Region";
        if ([2, 4, 5, 8].includes(regionId)) return "East Region";
        if (regionId === 3 || regionId === 6) return "PacNW Region";
        return "";
    };

    const topRepIdsByRegion = useMemo(() => {
        const bestByRegion = new Map();

        rows.forEach((r) => {
            const region = r.region?.trim();
            if (!region) return;

            const orders = Number(r.orderCount ?? 0);
            const revenue = Number(r.revenue ?? 0);

            const score = 0.5 * (orders / maxOrders) + 0.5 * (revenue / maxRevenue);

            const currentBest = bestByRegion.get(region);
            if (!currentBest || score > currentBest.score) {
                bestByRegion.set(region, { repId: r.repId, score });
            }
        });

        return new Set(Array.from(bestByRegion.values()).map((x) => x.repId));
    }, [rows, maxOrders, maxRevenue]);

    const columns = useMemo(() => ([
        { field: "name", headerName: "Name", flex: 250, editable: isManagementUser,
            renderCell: (params) => {
                const salesRep = repByName.get(params.value);
                const isTop = topRepIdsByRegion?.has(params.row.repId);

                return (
                    <div className="d-flex align-items-center">
                        {salesRep && (
                            <img alt="" width="50" height="50" className="rounded-circle me-2" src={FilePath(`/teamimages/${FormatNameToFilePath(`${salesRep.firstName} ${salesRep.lastName}`)}.png`)} />
                        )}

                        <span className="me-2">{params.value}</span>

                        {isTop && (
                            <MdOutlineStarRate className="mb-1" title="Top rep in region" />
                        )}
                    </div>
                );
            },

            renderEditCell: (params) => {
                if (params.row.repId < 0) {
                    return (
                        <Select
                            value={params.value || ""}
                            onChange={(event) => {
                                const selectedName = event.target.value;
                                params.api.setEditCellValue(
                                    { id: params.id, field: "name", value: selectedName },
                                    event
                                );
                            }}
                            fullWidth
                            size="small"
                        >
                            {salesReps.map((rep) => (
                                <MenuItem key={`${rep.firstName} ${rep.lastName}`} value={`${rep.firstName} ${rep.lastName}`}>
                                    {rep.firstName} {rep.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    );
                }

                return <span>{params.value}</span>;
            },
        },
        { field: "position", headerName: "Position", flex: 250, editable: false,
            renderCell: (params) => {
                const draft = params.api.getRowWithUpdatedValues(params.id) ?? params.row;

                if (draft.repId >= 0) return draft.position ?? params.value ?? "";

                const rep = repByName.get(draft.name);
                return rep?.title ?? "";
            },
        },
        { field: "region", headerName: "Region", flex: 150, editable: false,
            renderCell: (params) => {
                const draft = params.api.getRowWithUpdatedValues(params.id) ?? params.row;

                if (draft.repId >= 0) return draft.region ?? params.value ?? "";

                const rep = repByName.get(draft.name);
                const regionId = rep?.primaryOffice?.regionId;
                return regionId ? getRegionName(regionId) : "";
            },
        },
        { field: "revenue", headerName: "Revenue", flex: 150, editable: isManagementUser, type: "number", headerAlign: "left", align: "right",
            valueParser: (value) => Number(String(value ?? "").replace(/[^0-9.-]/g, "")) || 0,
            valueFormatter: (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0),
        },
        { field: "orderCount", headerName: "New Face Orders", flex: 150, editable: isManagementUser, type: "number", headerAlign: "left", align: "right",
            valueParser: (value) =>
                Number(String(value ?? "").replace(/[^0-9.-]/g, "")) || 0,
        },
        { field: "weightedScore", headerName: "Weighted Score", flex: 150, type: "number", sortable: true, headerAlign: "left", align: "right",
            valueGetter: (_, row) => {
                const orders = Number(row.orderCount ?? 0);
                const revenue = Number(row.revenue ?? 0);
                return 0.5 * (orders / maxOrders) + 0.5 * (revenue / maxRevenue);
            },
            valueFormatter: (value) =>  new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 0 }).format(value ?? 0),
        },
        { field: "rank", headerName: "Rank", flex: 90, sortable: true, type: "number", headerAlign: "left", align: "right",
            valueGetter: (_, row) => rankByRepId.get(row.repId) ?? null,
        },
        { field: "lastUpdated", headerName: "Last Updated", flex: 125 },
        ...(isManagementUser
            ? [
                {
                    field: "actions", type: "actions", headerName: "Actions", flex: 100, cellClassName: "actions", headerAlign: "left", align: "right",
                    getActions: ({ id }) => [
                        <GridActionsCellItem
                            key="delete"
                            icon={<MdDelete />}
                            label="Delete"
                            onClick={() => {
                                setRepToDelete(id);
                                setDeleted(false);
                            }}
                            color="inherit"
                            data-bs-toggle="modal"
                            data-bs-target="#deleteModal"
                            role="button"
                        />,
                    ],
                },
            ]
            : []),
    ]), [ isManagementUser, salesReps, repByName, topRepIdsByRegion, maxOrders, maxRevenue, rankByRepId, ]);

    const getEditingRowIds = () => {
        const fromGrid = new Set(Object.keys(apiRef.current?.state?.editRows ?? {}).map(Number));
        const fromModel = Object.entries(rowModesModel)
            .filter(([, m]) => m?.mode === GridRowModes.Edit)
            .map(([id]) => Number(id));

        return fromModel.filter((id) => fromGrid.has(id));
    };

    const fillPositionAndRegionFromRep = (r) => {
        if (!r?.name) return r;

        const rep = repByName.get(r.name);
        if (!rep) return r;

        const regionId = rep?.primaryOffice?.regionId;

        return {
            ...r,
            position: r.position || rep.title || "",
            region: r.region || (regionId ? getRegionName(regionId) : ""),
        };
    };

    const getUncommittedEdits = () =>
        getEditingRowIds()
            .map((id) => apiRef.current.getRowWithUpdatedValues(id))
            .filter(Boolean);

    const mergeByRepId = (base, updates) => {
        const map = new Map(base.map((r) => [r.repId, r]));
        updates.forEach((r) => map.set(r.repId, r));
        return Array.from(map.values());
    };

    const processRowUpdate = (newRow) => {
        const updatedRow = fillPositionAndRegionFromRep({ ...newRow });

        setRows((prev) =>
            prev.map((row) => (row.repId === updatedRow.repId ? updatedRow : row))
        );

        setModifiedRows((prev) => {
            const exists = prev.some((row) => row.repId === updatedRow.repId);
            return exists
                ? prev.map((row) => (row.repId === updatedRow.repId ? updatedRow : row))
                : [updatedRow, ...prev];
        });

        return updatedRow;
    };

    const addNewRecordInline = () => {
        idCounterRef.current -= 1;
        const newId = idCounterRef.current;

        const newRecord = {
            repId: newId,
            name: '',
            position: '',
            region: '',
            revenue: 0,
            orderCount: 0,
            lastUpdated: null,
        };

        setRows((prev) => [newRecord, ...prev]);
        setModifiedRows((prev) => [newRecord, ...prev]);

        setRowModesModel((prev) => ({
            ...prev,
            [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
        }));
    };

    const saveRepsOfTheYear = async () => {
        if (!isManagementUser || !apiRef.current) return;

        const uncommittedEdits = getUncommittedEdits();
        getEditingRowIds().forEach((id) => apiRef.current.stopRowEditMode({ id }));

        const payload = mergeByRepId(modifiedRows, uncommittedEdits)
            .map(fillPositionAndRegionFromRep)
            .filter((r) => r.name?.trim())
            .map(({ lastUpdated, ...rest }) => rest);

        if (!payload.length) return setError("Please select a sales rep before saving.");

        try {
            const { data } = await apiAdmin.post("/save-rep-info", payload);
            if (!data) {
                setError("There was an error saving the sales rep information. Please try again.");
                return;
            }

            setSaved(true);
            setModifiedRows([]);
            setRowModesModel({});
            await getRepsOfTheYear();
        } catch {
            setError("There was an error saving the sales rep information. Please try again.");
        }
    };

    const deleteRep = async () => {
        try {
            const res = await apiAdmin.post(`/delete-rep?repId=${repToDelete}`);
            if (res.status === 200) {
                setRepToDelete(null);
                setDeleted(true);
                await getRepsOfTheYear();
            } else {
                setError("There was an error deleting the rep. Please try again.");
            }
        } catch {
            setError("There was an error deleting the rep. Please try again.");
        }
    };

    function EditToolbar() {
        if (!isManagementUser) return null;

        return (
            <CustomDataGridToolbar>
                <span className="btn-link btn" onClick={addNewRecordInline}> <MdAdd className="mb-1" /> Add New Sales Rep</span>
            </CustomDataGridToolbar>
        );
    }

    return (
        <div>
            <title>Reps of the Year</title>
            <h1>Reps of the Year - Sales Reward Trip Tracker</h1>
            {deleted && <div className="alert alert-warning alert-dismissible" role="alert">Rep deleted successfully!<button type="button" className="btn-close" onClick={() => setDeleted(false)} aria-label="Close"></button></div>}
            {error && <div className="alert alert-danger alert-dismissible" role="alert">{error}<button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button></div>}
            <DataGridPro
                editMode="row"
                className="repsGrid"
                rows={rows}
                columns={columns}
                showToolbar
                slots={{ toolbar: EditToolbar }}
                isCellEditable={(params) => {
                    if (!isManagementUser) return false;
                    if (params.field === "name") return params.row.repId < 0; 
                    return true; 
                }}
                loading={loading}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                }}
                autoHeight
                rowHeight={56}
                disableRowSelectionOnClick

                getRowClassName={(params) => {
                    const r = (params.row.region || "").toLowerCase();
                    const regionClass =
                        r.includes("southwest") ? "region-southwest" :
                            r.includes("east") ? "region-east" :
                                r.includes("pacnw") ? "region-pacnw" : "";

                    const topClass = topRepIdsByRegion?.has(params.row.repId) ? "top-in-region" : "";
                    return [regionClass, topClass].filter(Boolean).join(" ");
                }}

                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 25, page: 0 }
                    },
                    sorting: {
                        sortModel: [
                            { field: "region", sort: "asc" },
                            { field: "rank", sort: "asc" },
                        ],
                    },
                }}
                disableMultipleColumnsSorting={false}
                pagination
                apiRef={apiRef}
                processRowUpdate={processRowUpdate}
                getRowId={(row) => row.repId}
                rowModesModel={rowModesModel}
                onRowModesModelChange={setRowModesModel}
            />
            {isManagementUser && (
                <button className="btn btn-primary m-2" onClick={saveRepsOfTheYear}>
                    Save
                </button>
            )}
            {saved && (
                <div className="modal shown d-block" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-body">
                                <p>Rep Information saved successfully!</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-primary" onClick={() => setSaved(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="modal shown" tabIndex="-1" id="deleteModal" >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete rep?</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this sales rep?</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">No, Cancel</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={() => deleteRep()}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RepsOfTheYear;
